/**
 * Inactivity Monitor Component
 * 
 * INDUSTRY BEST PRACTICES IMPLEMENTATION:
 * 1. Timestamp-based tracking (immune to browser throttling)
 * 2. Page Visibility API integration (handle tab switching)
 * 3. Cross-tab synchronization via localStorage
 * 4. Graceful handling of hidden tabs
 * 5. Accurate countdown using requestAnimationFrame
 * 6. Edge case handling (browser sleep, system time changes)
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { clearAuthData, isAuthenticated } from '../utils/tokenUtils';
import { safeStorage } from '../utils/safeStorage';
import { authRedirect } from '../utils/authRedirect';

// Configuration constants
// Production: 30 minutes (HIPAA requirement — do NOT lengthen).
// Development (localhost): 8 hours, so the inactivity timer doesn't sign devs
// out while they're heads-down or in another window. Use Vite's import.meta.env.DEV
// (process.env.NODE_ENV is not reliably replaced in the browser bundle and was
// silently falling through to the 30-minute production value on localhost).
const INACTIVITY_TIMEOUT = import.meta.env.DEV
  ? 8 * 60 * 60 * 1000   // 8 hours in dev
  : 30 * 60 * 1000;      // 30 minutes in production
const WARNING_BEFORE_TIMEOUT = 60 * 1000;   // Show warning 60 seconds before logout
const PROMPT_BEFORE_IDLE = WARNING_BEFORE_TIMEOUT; // Prompt timing
const STORAGE_KEY = 'evv_last_activity_timestamp';

/**
 * CRITICAL: Custom events list for react-idle-timer
 * The default events list does NOT include 'input' or 'change' events!
 * This was causing users to be logged out while actively typing in form fields.
 * 
 * Industry Best Practice: Include ALL user interaction events to detect activity
 */
const ACTIVITY_EVENTS = [
  // Mouse events
  'mousemove',
  'mousedown',
  'mouseup',
  'click',
  'dblclick',
  'wheel',
  'DOMMouseScroll',
  'mousewheel',
  // Keyboard events - CRITICAL for detecting typing
  'keydown',
  'keypress',
  'keyup',
  // Touch events (mobile/tablet)
  'touchstart',
  'touchmove',
  'touchend',
  // Form input events - CRITICAL: These were missing!
  'input',           // Fires when input value changes (typing, paste, etc.)
  'change',          // Fires when select/checkbox changes
  'focus',           // User clicked into a field
  'blur',            // User left a field
  // Pointer events (modern touch/stylus)
  'pointerdown',
  'pointermove',
  'pointerup',
  'MSPointerDown',
  'MSPointerMove',
  // Page visibility
  'visibilitychange',
  // Scroll events
  'scroll',
  // Clipboard events
  'paste',
  'cut',
  'copy',
];

const InactivityMonitor = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(isAuthenticated());
  const rafRef = useRef(null);
  const warningShownRef = useRef(false);

  // Debug logging only in development
  if (process.env.NODE_ENV === 'development') {
    console.log('InactivityMonitor mounted, authenticated:', isUserAuthenticated);
  }

  /**
   * Update last activity timestamp in localStorage
   * This ensures cross-tab synchronization
   * Called on every activity event via onAction callback (throttled to 500ms)
   */
  const updateActivityTimestamp = useCallback(() => {
    const now = Date.now();
    safeStorage.setItem(STORAGE_KEY, now.toString());
    // Don't log on every activity - too noisy
  }, []);

  /**
   * Get remaining time until logout
   * Returns: { remainingMs, isExpired, shouldWarn }
   */
  const getRemainingTime = useCallback(() => {
    const lastActivity = parseInt(safeStorage.getItem(STORAGE_KEY) || Date.now().toString());
    const elapsed = Date.now() - lastActivity;
    const remainingMs = INACTIVITY_TIMEOUT - elapsed;
    const isExpired = remainingMs <= 0;
    const shouldWarn = remainingMs <= WARNING_BEFORE_TIMEOUT && remainingMs > 0;
    
    return { remainingMs, isExpired, shouldWarn, elapsed };
  }, []);

  /**
   * Handle user becoming idle
   * Show warning modal ONLY if tab is visible
   * Note: Prompt shows 60 seconds before timeout (29 min in prod, 59 min in dev)
   */
  const onPrompt = useCallback(() => {
    // Don't show warning if tab is hidden
    if (document.hidden) {
      console.log('⚠️ User idle but tab hidden - skipping warning modal');
      warningShownRef.current = false;
      return;
    }

    console.log('⚠️ User idle - showing warning modal');
    warningShownRef.current = true;
    setShowWarning(true);
    setCountdown(60); // Production: 60 seconds warning countdown

    // Use requestAnimationFrame for accurate countdown
    // More reliable than setInterval, especially on hidden tabs
    let lastTime = Date.now();
    let accumulatedTime = 0;

    const tick = () => {
      const now = Date.now();
      const delta = now - lastTime;
      lastTime = now;
      accumulatedTime += delta;

      if (accumulatedTime >= 1000) {
        accumulatedTime -= 1000;
        setCountdown((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  /**
   * Handle automatic logout after countdown expires
   */
  const onIdle = useCallback(() => {
    console.log('🚪 Inactivity timeout - logging out user');
    
    // Clear countdown RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // Clear warning state
    setShowWarning(false);
    warningShownRef.current = false;

    // Clear auth and redirect
    clearAuthData();
    safeStorage.removeItem(STORAGE_KEY);
    authRedirect('/login', 'inactivity');
  }, []);

  /**
   * Handle user activity (resets timer)
   */
  const onActive = useCallback(() => {
    console.log('✅ User active - timer reset');
    
    // Update timestamp for cross-tab sync
    updateActivityTimestamp();
    
    // Clear warning if shown
    if (showWarning) {
      setShowWarning(false);
      warningShownRef.current = false;
      
      // Clear countdown RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    }
  }, [showWarning, updateActivityTimestamp]);

  /**
   * Page Visibility API - Check timeout status when tab becomes visible
   * This handles the case where user was on another tab and timeout expired
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isUserAuthenticated) {
        console.log('👁️ Tab became visible - checking timeout status');
        
        const { isExpired, shouldWarn, remainingMs } = getRemainingTime();
        
        if (isExpired) {
          console.log('⏰ Timeout expired while tab was hidden - logging out');
          onIdle();
        } else if (shouldWarn && !warningShownRef.current) {
          console.log('⚠️ Warning period while tab visible - showing modal');
          onPrompt();
        } else if (!shouldWarn && showWarning) {
          // User became active in another tab, clear warning
          console.log('✅ Activity detected in another tab - clearing warning');
          setShowWarning(false);
          warningShownRef.current = false;
          if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
          }
        }
        
        console.log(`⏱️ Time remaining: ${Math.floor(remainingMs / 1000)}s`);
      } else if (document.hidden && showWarning) {
        // Tab is hidden and warning is showing - hide it
        console.log('🙈 Tab hidden - hiding warning modal');
        setShowWarning(false);
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [getRemainingTime, onIdle, onPrompt, showWarning]);

  /**
   * Initialize activity timestamp on mount
   */
  useEffect(() => {
    if (isUserAuthenticated) {
      // Initialize timestamp if not exists
      if (!safeStorage.getItem(STORAGE_KEY)) {
        updateActivityTimestamp();
      }
    }
  }, [updateActivityTimestamp, isUserAuthenticated]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  /**
   * Initialize idle timer with react-idle-timer
   * CRITICAL: Timer continues running even when tab is hidden
   * Timeout: 30 min (production) / 60 min (development)
   */
  // Update authentication state using storage event (more efficient than polling)
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = isAuthenticated();
      setIsUserAuthenticated(authStatus);
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Auth status checked:', authStatus);
      }
    };

    // Check immediately on mount
    checkAuth();

    /**
     * Listen for localStorage changes (login/logout in other tabs)
     * More efficient than polling every 3 seconds
     */
    const handleStorageChange = (e) => {
      if (e.key === 'evv_token' || e.key === null) {
        // Token changed or storage was cleared
        checkAuth();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Also check on visibility change (user returns to tab)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkAuth();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  /**
   * Called on EVERY activity event (throttled)
   * Updates localStorage timestamp for cross-tab sync and debug widget
   */
  const onAction = useCallback(() => {
    updateActivityTimestamp();
  }, [updateActivityTimestamp]);

  const { activate, getRemainingTime: getTimerRemaining } = useIdleTimer({
    onPrompt,
    onIdle,
    onActive,
    onAction, // CRITICAL: Update localStorage on every activity for accurate tracking
    timeout: INACTIVITY_TIMEOUT,
    promptBeforeIdle: PROMPT_BEFORE_IDLE,
    throttle: 500, // Throttle activity events to 500ms
    eventsThrottle: 200, // Throttle DOM events
    disabled: !isUserAuthenticated, // Only track when authenticated - uses STATE
    syncTimers: 200, // Sync across tabs
    crossTab: true, // Cross-tab communication
    stopOnIdle: false, // Keep timer running even when idle
    startManually: false, // Start automatically when mounted
    events: ACTIVITY_EVENTS, // CRITICAL: Use custom events to detect ALL user activity including form input
  });

  // Debug logging only in development
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 IdleTimer initialized, disabled:', !isUserAuthenticated);
  }

  // Debug: Log remaining time periodically (development only)
  useEffect(() => {
    // Only run debug logging in development
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    if (!isUserAuthenticated) {
      console.log('⏱️ Timer is disabled - user not authenticated');
      return;
    }

    // Log every 60 seconds (dev only — avoids flooding the production console)
    const interval = setInterval(() => {
      if (import.meta.env.DEV && getTimerRemaining) {
        const remaining = getTimerRemaining();
        console.log(`⏱️ Inactivity timer: ${Math.floor(remaining / 60000)}m ${Math.floor((remaining % 60000) / 1000)}s remaining`);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [getTimerRemaining, isUserAuthenticated]);

  /**
   * Handle "I'm Here" button click
   */
  const handleContinue = useCallback(() => {
    console.log('👤 User clicked "I\'m Here" - resetting timer');
    
    // Update activity timestamp
    updateActivityTimestamp();
    
    // Close modal
    setShowWarning(false);
    warningShownRef.current = false;
    
    // Clear countdown RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    
    // Activate timer (resets it)
    activate();
  }, [activate, updateActivityTimestamp]);

  // Don't render if not authenticated
  if (!isAuthenticated()) {
    return null;
  }

  // Render warning modal
  if (showWarning) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
        }}
      >
        <div
          className="bg-white"
          style={{
            padding: '2rem',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            maxWidth: '400px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏰</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1F2937' }}>
            Are you still there?
          </h2>
          <p style={{ color: '#6B7280', marginBottom: '1rem' }}>
            You'll be logged out in <strong>{countdown} seconds</strong> due to inactivity.
          </p>
          <button
            onClick={handleContinue}
            style={{
              backgroundColor: '#10B981',
              color: 'white',
              padding: '0.75rem 2rem',
              borderRadius: '0.375rem',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              width: '100%',
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = '#059669')}
            onMouseOut={(e) => (e.target.style.backgroundColor = '#10B981')}
          >
            Yes, I'm Here!
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default InactivityMonitor;
